/**
 * Sidebar navigation for documentation pages.
 *
 * Collapsible nested groups with active state highlighting.
 * Auto-expands the group containing the active page.
 * Includes a mobile hamburger toggle using shadcn Sheet.
 */

import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronRight, ExternalLink, Menu } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

interface NavItem {
	label: string;
	href: string;
}

interface NavGroup {
	title: string;
	/** Direct page items (rendered as links). */
	items?: NavItem[];
	/** Nested sub-groups (collapsible sections with their own items). */
	children?: NavGroup[];
}

export const NAV_GROUPS: NavGroup[] = [
	{
		title: "Getting Started",
		items: [
			{ label: "Introduction", href: "/docs/introduction" },
			{ label: "Quick Start", href: "/docs/quickstart" },
			{ label: "Core Concepts", href: "/docs/core-concepts" },
			{ label: "CLI", href: "/docs/cli" },
		],
	},
	{
		title: "Teammates",
		items: [{ label: "Teammates", href: "/docs/teammates" }],
		children: [
			{
				title: "Trigger Runs",
				items: [
					{ label: "Message", href: "/docs/runs" },
					{ label: "Webhook", href: "/docs/webhook-triggers" },
					{ label: "Email Inbox", href: "/docs/email-inbox" },
				],
			},
		],
	},
	{
		title: "Tasks",
		items: [{ label: "Tasks", href: "/docs/tasks" }],
		children: [
			{
				title: "Run Tasks",
				items: [
					{ label: "Manually", href: "/docs/runs" },
					{ label: "Scheduling", href: "/docs/scheduling" },
					{ label: "Webhook Trigger", href: "/docs/webhook-triggers" },
				],
			},
		],
	},
	{
		title: "Runs",
		items: [
			{ label: "Runs & Follow-ups", href: "/docs/runs" },
			{ label: "Streaming & Events", href: "/docs/streaming" },
		],
	},
	{
		title: "Webhooks",
		items: [{ label: "Webhook Events", href: "/docs/webhooks-guide" }],
	},
	{
		title: "Memories",
		items: [{ label: "Memories", href: "/docs/memories" }],
	},
	{
		title: "Tools",
		items: [{ label: "Tools", href: "/docs/skills" }],
	},
	{
		title: "Permissions",
		items: [{ label: "Permissions", href: "/docs/permissions" }],
	},
	{
		title: "Files",
		items: [{ label: "Files", href: "/docs/files" }],
	},
	{
		title: "API Reference",
		items: [
			{ label: "Introduction", href: "/docs/api-introduction" },
			{ label: "Pagination", href: "/docs/api-pagination" },
			{ label: "Errors", href: "/docs/api-errors" },
			{ label: "Endpoints", href: "/docs/api-reference" },
			{ label: "Users (Multi-tenancy)", href: "/docs/users" },
		],
	},
];

/** External link rendered at the bottom of the sidebar. */
export const LLM_DOCS_LINK = { label: "llms.txt", href: "/llms.txt" } as const;

/** Collect all NavItems from the tree (supports nested children). */
function collectItems(groups: NavGroup[]): NavItem[] {
	const result: NavItem[] = [];
	for (const g of groups) {
		if (g.items) result.push(...g.items);
		if (g.children) result.push(...collectItems(g.children));
	}
	return result;
}

/** Flat list of all navigation items for prev/next lookup (deduplicated by href). */
export const ALL_NAV_ITEMS: NavItem[] = collectItems(NAV_GROUPS).filter(
	(item, index, arr) => arr.findIndex((i) => i.href === item.href) === index
);

/** Check if a group (or its children) contains the current path. */
function groupContainsPath(group: NavGroup, path: string): boolean {
	if (group.items?.some((item) => item.href === path)) return true;
	if (group.children?.some((child) => groupContainsPath(child, path))) return true;
	return false;
}

function NavGroupSection({
	group,
	currentPath,
	depth = 0,
}: {
	group: NavGroup;
	currentPath: string;
	depth?: number;
}) {
	const containsActive = groupContainsPath(group, currentPath);
	const [expanded, setExpanded] = useState(containsActive);

	useEffect(() => {
		if (containsActive) setExpanded(true);
	}, [containsActive]);

	const isTopLevel = depth === 0;
	const hasContent =
		(group.items && group.items.length > 0) || (group.children && group.children.length > 0);

	return (
		<div className={cn(isTopLevel && "mb-4")}>
			<button
				type="button"
				className={cn(
					"flex w-full cursor-pointer select-none items-center gap-1 text-sm font-semibold text-foreground transition-colors hover:text-primary",
					isTopLevel
						? "mb-1.5 px-1"
						: "mb-1 px-3 py-1 text-xs font-medium uppercase tracking-wider text-muted-foreground"
				)}
				onClick={() => hasContent && setExpanded(!expanded)}
			>
				{hasContent && (
					<ChevronRight
						className={cn(
							"h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200",
							expanded && "rotate-90"
						)}
					/>
				)}
				{group.title}
			</button>

			{expanded && (
				<>
					{group.items && (
						<ul className="space-y-0.5">
							{group.items.map((item) => (
								<li key={item.href}>
									<Link
										to={item.href}
										className={cn(
											"block rounded-md px-3 py-1.5 text-sm transition-colors",
											isTopLevel ? "ml-2" : "ml-4",
											currentPath === item.href
												? "bg-primary/10 font-medium text-primary"
												: "text-muted-foreground hover:bg-muted hover:text-foreground"
										)}
									>
										{item.label}
									</Link>
								</li>
							))}
						</ul>
					)}
					{group.children?.map((child) => (
						<NavGroupSection
							key={child.title}
							group={child}
							currentPath={currentPath}
							depth={depth + 1}
						/>
					))}
				</>
			)}
		</div>
	);
}

function SidebarNav() {
	const routerState = useRouterState();
	const currentPath = routerState.location.pathname;

	return (
		<nav className="space-y-1">
			{NAV_GROUPS.map((group) => (
				<NavGroupSection key={group.title} group={group} currentPath={currentPath} />
			))}
			<div className="mt-4 border-t pt-4">
				<a
					href={LLM_DOCS_LINK.href}
					target="_blank"
					rel="noopener noreferrer"
					className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
				>
					<ExternalLink className="h-3.5 w-3.5" />
					{LLM_DOCS_LINK.label}
				</a>
			</div>
		</nav>
	);
}

/** Desktop sidebar — sticky positioned. */
export function DocsSidebar() {
	return (
		<div className="sticky top-24 max-h-[calc(100vh-6rem)] overflow-y-auto">
			<SidebarNav />
		</div>
	);
}

/** Mobile sidebar — hamburger toggle with Sheet overlay. */
export function DocsMobileSidebar() {
	const [open, setOpen] = useState(false);

	return (
		<Sheet open={open} onOpenChange={setOpen}>
			<SheetTrigger asChild>
				<Button variant="ghost" size="icon" className="lg:hidden">
					<Menu className="h-5 w-5" />
					<span className="sr-only">Open docs navigation</span>
				</Button>
			</SheetTrigger>
			<SheetContent side="left" className="w-60 p-6">
				<SheetTitle className="mb-4 text-lg font-semibold">Documentation</SheetTitle>
				{/* biome-ignore lint/a11y/useKeyWithClickEvents: Sheet handles keyboard events */}
				{/* biome-ignore lint/a11y/noStaticElementInteractions: Sheet handles keyboard events */}
				<div onClick={() => setOpen(false)}>
					<SidebarNav />
				</div>
			</SheetContent>
		</Sheet>
	);
}
